import { motion } from 'framer-motion';
import { ArrowRight, Trophy } from 'lucide-react';
import ScoreDisplay from '@/components/ScoreDisplay';
import ScoreTimeline from '@/components/ScoreTimeline';

function getInterpretation(recScore, healthScore) {
  const recHigh = recScore >= 50;
  const healthHigh = healthScore >= 50;
  if (recHigh && healthHigh) return "You're doing great. AI engines are mentioning you, and your presence is strong. Keep monitoring.";
  if (!recHigh && healthHigh) return "The work is done. Your presence is solid — AI results are building. Give it time.";
  if (recHigh && !healthHigh) return "You're getting AI mentions, but your position is fragile. Strengthen your foundation in the Action Center.";
  return "Let's get to work. Your Action Center has personalized tasks to build your AI presence.";
}

export default function DashboardScoreRings({ score, presenceHealth, benchmarks, navigate }) {
  return (
    <>
      {/* Score Hero — Dual Ring Layout */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="py-6">
        <div className="flex items-center justify-center gap-8 md:gap-12">
          {/* Left Ring: AI Recommendation Score (THE RESULT) */}
          <button onClick={() => navigate('/visibility')} className="flex flex-col items-center group" data-testid="recommendation-score-ring">
            <p className="text-[10px] uppercase tracking-widest mb-3 group-hover:opacity-70 transition-opacity" style={{ color: '#1D6B4F' }}>AI Recommendation Score</p>
            <ScoreDisplay score={score.total || 0} color="#1D6B4F" size="large" />
            <p className="text-[10px] mt-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#8C7E6E' }}>The result →</p>
          </button>
          {/* Right Ring: AI Presence Health (THE EFFORT) */}
          <button onClick={() => navigate('/presence')} className="flex flex-col items-center group" data-testid="presence-health-ring">
            <p className="text-[10px] uppercase tracking-widest mb-3 group-hover:opacity-70 transition-opacity" style={{ color: '#B8985A' }}>AI Presence Health</p>
            <ScoreDisplay score={presenceHealth?.score || 0} color="#B8985A" size="large" />
            <p className="text-[10px] mt-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#8C7E6E' }}>The effort →</p>
          </button>
        </div>
        {/* Contextual Interpretation */}
        <p className="text-[13px] mt-5 text-center max-w-md mx-auto leading-relaxed" style={{ color: '#5C5347' }} data-testid="score-interpretation">
          {getInterpretation(score.total || 0, presenceHealth?.score || 0)}
        </p>
        {/* Action Center link */}
        {presenceHealth?.score < 70 && (
          <div className="text-center mt-3">
            <button onClick={() => navigate('/actions')} className="text-[12px] font-medium inline-flex items-center gap-1 hover:opacity-70 transition-opacity" style={{ color: '#B8985A' }} data-testid="action-center-link">
              View your Action Center <ArrowRight size={12} />
            </button>
          </div>
        )}
      </motion.div>

      {/* Score Timeline */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}>
        <ScoreTimeline />
      </motion.div>

      {/* Score Breakdown */}
      {score.breakdown && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="p-5 rounded-sm bg-[#F7F4EF] mb-4" style={{ border: '1px solid #D4C9B8' }}>
          <h3 className="text-[13px] font-medium mb-4" style={{ color: '#3C3226' }}>Score Breakdown</h3>
          <div className="space-y-3">
            {Object.entries(score.breakdown).map(([key, val]) => {
              const labelMap = {
                'Directory Listings': 'Directory Guidance',
                'Directory Presence': 'Directory Guidance',
              };
              const displayLabel = labelMap[val.label] || val.label;
              return (
              <div key={key}>
                <div className="flex justify-between text-[13px] mb-1">
                  <span style={{ color: '#5C5347' }}>{displayLabel}</span>
                  <span className="font-medium" style={{ color: '#3C3226' }}>{val.score}/100</span>
                </div>
                <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: '#D4C9B8' }}>
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${val.score}%`, backgroundColor: val.score >= 60 ? '#1D6B4F' : val.score >= 35 ? '#E5A000' : '#D44' }} />
                </div>
              </div>
            )})}
          </div>
        </motion.div>
      )}

      {/* Industry Benchmarks */}
      {benchmarks && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}>
          <BenchmarkCard benchmarks={benchmarks} />
        </motion.div>
      )}
    </>
  );
}

function BenchmarkCard({ benchmarks }) {
  const { user_score, profession, top_percentage, benchmarks: b } = benchmarks;
  if (!b) return null;

  const avg = b.average;
  const top = b.top_score;
  const profLabel = profession ? profession.charAt(0).toUpperCase() + profession.slice(1) + 's' : 'professionals';
  const scopeLabel = b.scope === 'city' ? `in ${b.city}` : b.scope === 'state' ? `in ${b.state}` : 'nationally';
  const maxVal = Math.max(user_score, avg, top, 100);

  return (
    <div className="p-5 rounded-sm bg-[#F7F4EF] mb-4" style={{ border: '1px solid #D4C9B8' }} data-testid="benchmark-card">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-none flex items-center justify-center" style={{ backgroundColor: 'rgba(29,107,79,0.06)' }}>
          <Trophy size={16} strokeWidth={1.5} style={{ color: '#1D6B4F' }} />
        </div>
        <div>
          <h3 className="text-[13px] font-medium" style={{ color: '#3C3226' }}>Industry Benchmarks</h3>
          <p className="text-[12px]" style={{ color: '#8C7E6E' }}>{b.sample_size} {profLabel} {scopeLabel}</p>
        </div>
      </div>

      <div className="p-3 rounded-none mb-4" style={{ backgroundColor: 'rgba(29,107,79,0.04)' }} data-testid="benchmark-percentile">
        <p className="text-[13px]" style={{ color: '#3C3226' }}>
          You're in the <span className="font-semibold" style={{ color: '#1D6B4F' }}>top {top_percentage}%</span> of {profLabel} {scopeLabel}
        </p>
      </div>

      <div className="space-y-3">
        {[
          { label: 'Your score', value: user_score, color: '#C67A5C', bold: true },
          { label: `Average ${profession}`, value: avg, color: '#5C5347', bold: false },
          { label: 'Top performer', value: top, color: '#1D6B4F', bold: false },
        ].map(item => (
          <div key={item.label} data-testid={`benchmark-bar-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex justify-between text-[12px] mb-1">
              <span style={{ color: item.bold ? '#3C3226' : '#8C7E6E' }} className={item.bold ? 'font-medium' : ''}>{item.label}</span>
              <span className="font-medium" style={{ color: item.bold ? '#3C3226' : '#8C7E6E' }}>{item.value}</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#D4C9B8' }}>
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${(item.value / maxVal) * 100}%`, backgroundColor: item.color }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
